/**
 * @symbo.ls/fetch sdk adapter (registered, not built-in).
 *
 * Routes DOMQL `fetch:` declarations through @symbo.ls/sdk's EntityDispatcher
 * so workspace UI never speaks to a backend other than the SDK. Follows the
 * same `setup(options) → adapterObject` contract as the supabase / rest /
 * local builtin adapters, but ships from the workspace package because the
 * SDK is a peer concern (not a transport).
 *
 * Registration: call `registerSdkAdapter()` once at boot — it adds the
 * factory to @symbo.ls/fetch's runtime adapter registry. Then any project
 * config can opt in with:
 *
 *   fetch: { adapter: 'sdk', sdk: getSDK() }
 *   // or, if you'd rather hand over just the dispatcher:
 *   fetch: { adapter: 'sdk', executor: (from, op, args, cb) => sdk.execute(from, op, args, cb) }
 *
 * Argument shape — adapter packs every call into a single object whose
 * fields the EntityDispatcher's per-route `argMap` extracts into the
 * positional shape the underlying service method expects. Aliases are
 * provided so argMaps written for either `params`/`filter` or
 * `data`/`payload` work uniformly:
 *
 *   - select  → { filter, params, single, limit, offset, order, options, ...params }
 *   - insert  → { payload, data, ...params }
 *   - update  → { id, number, payload, data, ...params }
 *   - delete  → { id, number, ...params }
 *   - rpc     → params forwarded as-is
 *   - sub     → { ...params, filter }
 *
 * SDK init wait — the SDK proxy in shared/functions/sdk.js auto-awaits
 * SDK init for every method call, so `sdk.execute(...)` returns a Promise.
 * Subscribe handles this with a cancel-token shim: a sync unsubscribe fn
 * is returned immediately and the real teardown is wired up when the
 * dispatcher's promise resolves.
 *
 * FA204 / frank trap — _settle/_packSelect/_packCreate/_packUpdate/
 * _packDelete used to live at module scope, referenced bare from
 * buildSdkAdapter; registerSdkAdapter called buildSdkAdapter
 * bare too. Unresolvable once each export is individually frank.toJSON'd for
 * platform delivery (project_frank_drops_module_local_helpers).
 * buildSdkAdapter now nests its own helpers; registerSdkAdapter carries a
 * full self-contained inline copy of buildSdkAdapter instead of a bare
 * sibling call.
 */

import { registerAdapter } from '@symbo.ls/fetch'

// Build the adapter object synchronously from already-resolved options.
// metaFetchConfig.js calls this at construction time; the async `setup`
// export below wraps it for the @symbo.ls/fetch contract.
export const buildSdkAdapter = (options = {}) => {
  const _settle = (p) =>
    Promise.resolve(p)
      .then((d) => ({ data: d, error: null }))
      .catch((error) => ({ data: null, error }))

  const _packSelect = ({ params, single, limit, offset, order, options }) => ({
    filter: params,
    params,
    single,
    limit,
    offset,
    order,
    options,
    ...(params || {})
  })

  const _packCreate = ({ data, params }) => ({
    payload: data,
    data,
    ...(params || {})
  })

  const _packUpdate = ({ params, data }) => ({
    id: params?.id ?? params?.number,
    number: params?.number ?? params?.id,
    payload: data,
    data,
    ...(params || {})
  })

  const _packDelete = ({ params }) => ({
    id: params?.id ?? params?.number,
    number: params?.number ?? params?.id,
    ...(params || {})
  })

  const executor =
    typeof options.executor === 'function'
      ? options.executor
      : options.sdk && typeof options.sdk.execute === 'function'
        ? options.sdk.execute.bind(options.sdk)
        : null

  if (!executor) {
    throw new Error(
      '@symbo.ls/fetch sdk adapter: pass { sdk } (an SDK instance with .execute) ' +
        'or { executor } (a function with the same signature) in your fetch config.'
    )
  }

  const exec = (from, op, args, cb) => executor(from, op, args, cb)

  return {
    name: 'sdk',

    select: (cfg) =>
      _settle(exec(cfg.from, 'list', _packSelect(cfg))).then(({ data, error }) => ({
        data: cfg.single ? (Array.isArray(data) ? data[0] : data) : data,
        error
      })),

    rpc: (cfg) => _settle(exec(cfg.from, 'rpc', cfg.params)),

    insert: (cfg) => _settle(exec(cfg.from, 'create', _packCreate(cfg))),

    update: (cfg) => _settle(exec(cfg.from, 'update', _packUpdate(cfg))),

    // Upsert maps to the entity's `upsert` op. Entities that haven't
    // declared one will reject with `does not support op 'upsert'` from
    // the dispatcher — callers should branch on insert/update or wait
    // until the entity registers an `upsert` route. See
    // tickets/sdk.md SDK-FETCH-ADAPTER-1.
    upsert: (cfg) =>
      _settle(exec(cfg.from, 'upsert', { ..._packCreate(cfg), onConflict: cfg.onConflict })),

    delete: (cfg) => _settle(exec(cfg.from, 'remove', _packDelete(cfg))),

    // Subscribe must return a sync unsubscribe — fetch plugin assigns it
    // to ref.__unsubscribe immediately. SDK proxy may make exec() async;
    // cancel-token shim resolves both cases.
    subscribe: (cfg, cb) => {
      let unsub = null
      let cancelled = false
      Promise.resolve(
        exec(cfg.from, 'subscribe', { ...(cfg.params || {}), filter: cfg.params }, cb)
      )
        .then((sub) => {
          if (cancelled) {
            if (typeof sub === 'function') {
              try {
                sub()
              } catch (_) {}
            } else if (sub?.unsubscribe) {
              try {
                sub.unsubscribe()
              } catch (_) {}
            }
            return
          }
          unsub =
            typeof sub === 'function' ? sub : sub?.unsubscribe ? () => sub.unsubscribe() : null
        })
        .catch(() => {})
      return () => {
        cancelled = true
        if (typeof unsub === 'function') {
          try {
            unsub()
          } catch (_) {}
        }
      }
    },

    // Auth bridges — `fetch: { from: 'auth.session', method: 'subscribe' }`
    // routes through SDK's onAuthStateChange via the dispatcher.
    getSession: () => Promise.resolve(exec('auth.session', 'list')).catch(() => null),
    onAuthStateChange: (cb) => {
      let unsub = null
      let cancelled = false
      Promise.resolve(exec('auth.session', 'subscribe', null, cb))
        .then((sub) => {
          if (cancelled) {
            if (typeof sub === 'function') {
              try {
                sub()
              } catch (_) {}
            }
            return
          }
          unsub = typeof sub === 'function' ? sub : null
        })
        .catch(() => {})
      return () => {
        cancelled = true
        if (typeof unsub === 'function') {
          try {
            unsub()
          } catch (_) {}
        }
      }
    }
  }
}

// Convenience: register the adapter with @symbo.ls/fetch's runtime registry
// at boot. Call once from the shell entry — afterwards, project configs can
// opt in with `fetch: { adapter: 'sdk', sdk: ... }`.
//
// The legacy `registerSdkAdapter({ sdk })` form (closure-captured sdk) keeps
// working — the loader passes the captured sdk through to setup() so any
// existing wiring continues unchanged. New code should prefer the per-decl
// `{ adapter: 'sdk', sdk }` form, which matches how supabase / rest / local
// adapters are configured.
export const registerSdkAdapter = (boundOptions = {}) => {
  // FA204 / frank trap — self-contained inline copy of buildSdkAdapter, see
  // the file header. Kept identical to the export above.
  function _buildSdkAdapter(options = {}) {
    const _settle = (p) =>
      Promise.resolve(p)
        .then((d) => ({ data: d, error: null }))
        .catch((error) => ({ data: null, error }))
    const _packSelect = ({ params, single, limit, offset, order, options }) => ({
      filter: params,
      params,
      single,
      limit,
      offset,
      order,
      options,
      ...(params || {})
    })
    const _packCreate = ({ data, params }) => ({
      payload: data,
      data,
      ...(params || {})
    })
    const _packUpdate = ({ params, data }) => ({
      id: params?.id ?? params?.number,
      number: params?.number ?? params?.id,
      payload: data,
      data,
      ...(params || {})
    })
    const _packDelete = ({ params }) => ({
      id: params?.id ?? params?.number,
      number: params?.number ?? params?.id,
      ...(params || {})
    })
    const executor =
      typeof options.executor === 'function'
        ? options.executor
        : options.sdk && typeof options.sdk.execute === 'function'
          ? options.sdk.execute.bind(options.sdk)
          : null
    if (!executor) {
      throw new Error(
        '@symbo.ls/fetch sdk adapter: pass { sdk } (an SDK instance with .execute) ' +
          'or { executor } (a function with the same signature) in your fetch config.'
      )
    }
    const exec = (from, op, args, cb) => executor(from, op, args, cb)
    return {
      name: 'sdk',
      select: (cfg) =>
        _settle(exec(cfg.from, 'list', _packSelect(cfg))).then(({ data, error }) => ({
          data: cfg.single ? (Array.isArray(data) ? data[0] : data) : data,
          error
        })),
      rpc: (cfg) => _settle(exec(cfg.from, 'rpc', cfg.params)),
      insert: (cfg) => _settle(exec(cfg.from, 'create', _packCreate(cfg))),
      update: (cfg) => _settle(exec(cfg.from, 'update', _packUpdate(cfg))),
      upsert: (cfg) =>
        _settle(exec(cfg.from, 'upsert', { ..._packCreate(cfg), onConflict: cfg.onConflict })),
      delete: (cfg) => _settle(exec(cfg.from, 'remove', _packDelete(cfg))),
      subscribe: (cfg, cb) => {
        let unsub = null
        let cancelled = false
        Promise.resolve(
          exec(cfg.from, 'subscribe', { ...(cfg.params || {}), filter: cfg.params }, cb)
        )
          .then((sub) => {
            if (cancelled) {
              if (typeof sub === 'function') {
                try {
                  sub()
                } catch (_) {}
              } else if (sub?.unsubscribe) {
                try {
                  sub.unsubscribe()
                } catch (_) {}
              }
              return
            }
            unsub =
              typeof sub === 'function' ? sub : sub?.unsubscribe ? () => sub.unsubscribe() : null
          })
          .catch(() => {})
        return () => {
          cancelled = true
          if (typeof unsub === 'function') {
            try {
              unsub()
            } catch (_) {}
          }
        }
      },
      getSession: () => Promise.resolve(exec('auth.session', 'list')).catch(() => null),
      onAuthStateChange: (cb) => {
        let unsub = null
        let cancelled = false
        Promise.resolve(exec('auth.session', 'subscribe', null, cb))
          .then((sub) => {
            if (cancelled) {
              if (typeof sub === 'function') {
                try {
                  sub()
                } catch (_) {}
              }
              return
            }
            unsub = typeof sub === 'function' ? sub : null
          })
          .catch(() => {})
        return () => {
          cancelled = true
          if (typeof unsub === 'function') {
            try {
              unsub()
            } catch (_) {}
          }
        }
      }
    }
  }

  registerAdapter('sdk', () =>
    Promise.resolve({
      setup: (decl = {}) => _buildSdkAdapter({ ...boundOptions, ...decl })
    })
  )
}

// The eager `registerAdapter('sdk', …)` lives in `bootShell.js` (the
// universal boot file, top-level) rather than here — Parcel was
// tree-shaking the trailing module-side-effect statement from this
// file despite `sideEffects: true` in package.json, leaving the
// deployed bundle without an early-registered sdk adapter.
